import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ClipboardCheck, FileCheck2, ShieldCheck } from 'lucide-react';
import type { AdmissionPreAuthStatusDto } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateOnly } from '@/lib/utils';
import { Alert, Button, Spinner, StatusBadge } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ShellEmpty, ShellPanel, ShellSectionHeading } from '@/components/clinical-shell/ShellPanel';
import { AdmissionPreAuthReadiness } from '@/features/insurance/PreAuthReadiness';
import { admissionIsOpen, type Admission } from '../types';
import { preAuthState, type PreAuthState } from '../workspace-facts';

const STATE_TONE: Record<PreAuthState, 'success' | 'warning' | 'destructive' | 'muted' | 'info'> = {
  self_pay: 'muted',
  not_required: 'success',
  approved: 'success',
  pending: 'info',
  needed: 'destructive',
  unknown: 'muted',
};

export function PreAuthBadge({ state }: { state: PreAuthState }) {
  return <StatusBadge status={state} tone={STATE_TONE[state]} label={tr('ipd', `ws_preauth_${state}`)} />;
}

export function PreAuthSection({ admission, status, loading }: { admission: Admission; status: AdmissionPreAuthStatusDto | undefined; loading: boolean }) {
  const { can, hasModule } = useAuth();
  const toast = useToast();
  const state = preAuthState(admission, status);
  const showReadiness = Boolean(admission.payer) && hasModule('nphies') && can('claim.read', 'preauth.manage', 'nphies.preauth.send');
  const interimClaim = useMutation({
    mutationFn: () => post<{ number: string; periodFrom: string; periodTo: string; invoices: number }>(`/insurance/admissions/${admission.id}/interim-claim`, {}),
    onSuccess: (claim) =>
      toast.success(
        tr('ipd', 'interim_claim_x_drafted', { number: claim.number }),
        tr('ipd', 'ws_interim_claim_detail', { count: claim.invoices, from: claim.periodFrom, to: claim.periodTo }),
      ),
    onError: (error) => toast.error(tr('ipd', 'could_not_raise_the_interim_claim'), describeError(error)),
  });
  const mayClaim = admissionIsOpen(admission) && Boolean(admission.payer) && can('claim.manage');

  return (
    <>
      <ShellSectionHeading
        icon={ShieldCheck}
        title={tr('ipd', 'ws_nav_preauth')}
        description={admission.payer ? admission.payer.name : tr('ipd', 'ws_self_pay')}
        actions={
          <>
            {mayClaim && (
              <Button size="sm" variant="outline" onClick={() => interimClaim.mutate()} loading={interimClaim.isPending}>
                <FileCheck2 className="h-4 w-4" aria-hidden /> {tr('ipd', 'interim_claim')}
              </Button>
            )}
            <Link to="/insurance">
              <Button size="sm" variant="outline">
                {tr('ipd', 'open_insurance')}
              </Button>
            </Link>
          </>
        }
      />
      <ShellPanel icon={ShieldCheck} title={tr('ipd', 'ws_preauth_status')} actions={<PreAuthBadge state={state} />}>
        {loading && <Spinner />}
        {state === 'self_pay' && <ShellEmpty>{tr('ipd', 'ws_preauth_self_pay_desc')}</ShellEmpty>}
        {status && admission.payer && (
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs text-muted-foreground">{tr('ipd', 'ws_approved_until')}</dt>
              <dd className="font-medium">{status.approvedUntil ? dateOnly(status.approvedUntil) : '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{tr('ipd', 'ws_latest_approval')}</dt>
              <dd className="font-medium" dir="ltr">
                {status.latestApproval?.preauthNo ?? '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-muted-foreground">{tr('ipd', 'ws_requests_waiting')}</dt>
              <dd className="font-medium tabular">{status.pendingCount}</dd>
            </div>
          </dl>
        )}
        {status?.message && state !== 'approved' && (
          <Alert variant={state === 'needed' ? 'warning' : 'default'} className="mt-3">
            {status.message}
          </Alert>
        )}
      </ShellPanel>
      {showReadiness && (
        <ShellPanel icon={ClipboardCheck} title={tr('billing', 'nr_title')} description={tr('billing', 'nr_intro')}>
          <AdmissionPreAuthReadiness admissionId={admission.id} />
        </ShellPanel>
      )}
    </>
  );
}
